import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import style from './MainProfile.module.css'
import LocationIcon2 from '../../../../assets/svgs/Location2'
import About from './About'
import Experience from './Experience'
import Education from './Education'
import Languages from './Languages'
import CVInsights from './CVInsights'
import EditIcon from '../../../../assets/svgs/EditIcon'
import PlusCircleIcon from '../../../../assets/svgs/PlusCircle'
import useWindowWidth from '../../../../hooks/useWindowWidth'

const urlLocal = process.env.REACT_APP_API_URL || ''

const CameraIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="13" r="4" stroke="#fff" strokeWidth="2"/>
  </svg>
)

const ImageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2"/>
    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
    <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const UploadCVIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="rgba(97,72,124,0.45)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="14,2 14,8 20,8" stroke="rgba(97,72,124,0.45)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="18" x2="12" y2="12" stroke="rgba(97,72,124,0.45)" strokeWidth="1.5" strokeLinecap="round"/>
    <polyline points="9,15 12,12 15,15" stroke="rgba(97,72,124,0.45)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
}

/* Empty state shown when a section has no data */
const EmptySection = ({ title, owner, onAdd, onUploadCV }) => (
  <div className={style.emptySection}>
    <div className={style.emptySectionHeader}>
      <p className={style.title}>{title}</p>
      {owner && (
        <div className={style.plus} onClick={onAdd} title={`Add ${title.toLowerCase()}`}>
          <PlusCircleIcon color="white" />
        </div>
      )}
    </div>
    {owner && (
      <div className={style.emptySectionBody}>
        <UploadCVIcon />
        <p className={style.emptySectionText}>No {title.toLowerCase()} yet</p>
        <button className={style.uploadCvBtn} onClick={onUploadCV}>
          Upload CV to auto-fill
        </button>
      </div>
    )}
  </div>
)

const MainProfile = ({
  userProfile,
  toggleModal,
  openModal,
  owner,
  editMode,
  editModelToggle,
}) => {
  const navigate = useNavigate()
  const [profileImage, setProfileImage] = useState(null)
  const [coverSrc, setCoverSrc] = useState(null)
  const [uploading, setUploading] = useState(false)
  const profileInputRef = useRef(null)
  const coverInputRef = useRef(null)
  const innerWidth = useWindowWidth()

  const avatarUrl = (uid, type) => `${urlLocal}/users/avatar/${uid}/${type}`

  useEffect(() => {
    const uid = userProfile?.user?.user_id
    if (!uid) return
    if (userProfile?.user?.profile_picture_url) {
      setProfileImage(avatarUrl(uid, 'profile'))
    }
    if (userProfile?.user?.cover_image_url) {
      setCoverSrc(avatarUrl(uid, 'cover'))
    }
  }, [userProfile?.user?.profile_picture_url, userProfile?.user?.cover_image_url])

  const uploadImage = async (file, type) => {
    if (!file) return null
    const formData = new FormData()
    formData.append('image', file)
    formData.append('type', type)
    setUploading(true)
    try {
      const res = await fetch(`${urlLocal}/users/upload-avatar`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })
      const data = await res.json()
      if (!res.ok) { console.error('Avatar upload failed:', data); return null }
      return data.path ? true : null
    } catch (err) {
      console.error('Avatar upload error:', err)
      return null
    } finally {
      setUploading(false)
    }
  }

  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => setProfileImage(ev.target.result)
    reader.readAsDataURL(file)
    const uid = userProfile?.user?.user_id
    const ok = await uploadImage(file, 'profile')
    if (ok && uid) setProfileImage(`${avatarUrl(uid, 'profile')}?t=${Date.now()}`)
  }

  const handleCoverImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => setCoverSrc(ev.target.result)
    reader.readAsDataURL(file)
    const uid = userProfile?.user?.user_id
    const ok = await uploadImage(file, 'cover')
    if (ok && uid) setCoverSrc(`${avatarUrl(uid, 'cover')}?t=${Date.now()}`)
  }

  const goToUploadCV = () => navigate('/dashboard?tab=upload')

  const firstName = userProfile?.user?.first_name || ''
  const lastName  = userProfile?.user?.last_name  || ''
  const title     = userProfile?.user?.user_title  || ''
  const locationText = userProfile?.user?.user_location || ''

  const hasAbout      = userProfile?.about?.length > 0
  const hasExperience = userProfile?.experience?.length > 0
  const hasEducation  = userProfile?.education?.length > 0
  const hasLanguages  = userProfile?.languages?.length > 0

  return (
    <motion.div
      className={`${style.container} ${editMode ? style.container_editMode : ''}`}
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
    >
      <div className={style.orb1} />
      <div className={style.orb2} />
      <div className={style.orb3} />

      {/* ── Cover / hero card ── */}
      <motion.div className={style.coverWrapper} variants={fadeUp} custom={0}>
        {coverSrc
          ? <img src={coverSrc} className={style.coverImage} alt="cover" />
          : <div className={style.defaultCover} />
        }
        <div className={style.imageCover} />

        {owner && (
          <>
            <button
              className={style.coverEditBtn}
              onClick={() => coverInputRef.current?.click()}
              disabled={uploading}
            >
              <ImageIcon />
              {uploading ? 'Uploading…' : 'Change cover'}
            </button>
            <input ref={coverInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoverImageChange} />
          </>
        )}

        <div
          className={style.iconContainer}
          onClick={() => owner && profileInputRef.current?.click()}
          style={{ cursor: owner ? 'pointer' : 'default' }}
        >
          {profileImage ? (
            <img src={profileImage} className={style.profilePicture} alt="profile" />
          ) : (
            <svg width={innerWidth < 768 ? 32 : 52} height={innerWidth < 768 ? 32 : 52} viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="8" r="4" fill="rgba(255,255,255,0.9)" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" fill="rgba(255,255,255,0.6)" />
            </svg>
          )}
          {owner && (
            <div className={style.addImage}>
              <CameraIcon />
              <p className={style.addImageLabel}>Upload</p>
            </div>
          )}
        </div>
        {owner && (
          <input ref={profileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfileImageChange} />
        )}

        <div className={style.userInfo}>
          <div className={style.userDetails}>
            <p className={style.userName}>
              {firstName} {lastName}
              {owner && editMode && (
                <span
                  onClick={() => toggleModal('profile', userProfile.user)}
                  className={style.profileEdit}
                  title="Edit profile"
                >
                  <EditIcon />
                </span>
              )}
            </p>
            {title        && <p className={style.userOccupation}>{title}</p>}
            {locationText && (
              <div className={style.userLocations}>
                <LocationIcon2 width={14} />
                {locationText}
              </div>
            )}
          </div>

          {owner && (
            <div className={style.scoreContainer}>
              <button className={style.button} onClick={editModelToggle}>
                {editMode ? 'Done' : 'Edit profile'}
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── About ── */}
      <motion.div variants={fadeUp} custom={1}>
        {hasAbout ? (
          <About
            title={`${firstName}`}
            userProfile={userProfile}
            openModal={openModal}
            toggleModal={toggleModal}
            owner={owner}
            removeComponent={() => {}}
            editMode={editMode}
          />
        ) : (
          owner && (
            <EmptySection
              title="About"
              owner={owner}
              onAdd={() => toggleModal('about', userProfile?.about?.[0] ?? {}, 'create')}
              onUploadCV={goToUploadCV}
            />
          )
        )}
      </motion.div>

      {/* ── Experience ── */}
      <motion.div variants={fadeUp} custom={2}>
        {hasExperience ? (
          <Experience
            userProfile={userProfile}
            toggleModal={toggleModal}
            owner={owner}
            removeComponent={() => {}}
            editMode={editMode}
          />
        ) : (
          owner && (
            <EmptySection
              title="Experience"
              owner={owner}
              onAdd={() => toggleModal('experience', userProfile, 'create')}
              onUploadCV={goToUploadCV}
            />
          )
        )}
      </motion.div>

      {/* ── Education ── */}
      <motion.div variants={fadeUp} custom={3}>
        {hasEducation ? (
          <Education
            userProfile={userProfile}
            toggleModal={toggleModal}
            owner={owner}
            removeComponent={() => {}}
            editMode={editMode}
          />
        ) : (
          owner && (
            <EmptySection
              title="Education"
              owner={owner}
              onAdd={() => toggleModal('education', userProfile, 'create')}
              onUploadCV={goToUploadCV}
            />
          )
        )}
      </motion.div>

      {/* ── Languages ── */}
      <motion.div variants={fadeUp} custom={4}>
        {hasLanguages ? (
          <Languages
            userProfile={userProfile}
            toggleModal={toggleModal}
            owner={owner}
            removeComponent={() => {}}
            editMode={editMode}
          />
        ) : (
          owner && (
            <EmptySection
              title="Languages"
              owner={owner}
              onAdd={() => toggleModal('language', userProfile, 'create')}
              onUploadCV={goToUploadCV}
            />
          )
        )}
      </motion.div>

      {/* ── CV Insights (owner only, data from backend) ── */}
      <motion.div variants={fadeUp} custom={5}>
        <CVInsights owner={owner} />
      </motion.div>
    </motion.div>
  )
}

export default MainProfile
